import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Trash2 } from 'lucide-react';
import { HoldRepeatButton } from '../HoldRepeatButton';

export const EditOrderDialog = ({
  open, onOpenChange, selectedOrder,
  editItems, products, subBrands,
  editDeliveryDate, setEditDeliveryDate, editDeliveryTime, setEditDeliveryTime,
  productDiscountByPid, fmtDate,
  // item handlers
  setEditItemSubBrand, setEditItemParent, setEditItemFlag, setEditItemWeight, setEditItemVariant,
  updateEditItemQty, setEditItemQty, setEditItemDiscount, setEditItemPrice,
  removeEditItem, addEditItem,
  // submit
  onSave,
  isStrictAdmin = false,
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent
      className="max-w-2xl max-h-[90vh] flex flex-col p-0"
      data-testid="edit-order-dialog"
      onOpenAutoFocus={(e) => e.preventDefault()}
    >
      <DialogHeader className="px-6 pt-6 pb-2 flex-shrink-0">
        <DialogTitle className="flex items-center justify-between gap-3 flex-wrap">
          <span>Edit Order - #{selectedOrder?.id?.slice(0, 8)}</span>
          <span className="text-sm font-bold" style={{ color: '#2B5339' }} data-testid="edit-order-total-qty">
            Total Qty: {editItems.reduce((s, i) => s + (Number(i.quantity) || 0), 0).toLocaleString('en-IN')}
          </span>
        </DialogTitle>
      </DialogHeader>
      <form className="flex-1 flex flex-col min-h-0" onSubmit={(e) => { e.preventDefault(); onSave(); }}>
      <div className="flex-1 overflow-y-auto px-6 py-2">
        <div className="space-y-3">
          {editItems.map((item, idx) => (
            <div key={idx} className="flex flex-wrap items-center gap-3 p-3 rounded border" style={{ borderColor: '#E2DFD8' }} data-testid="edit-order-item">
              <div className="basis-full min-w-0">
                <p className="text-sm font-medium break-words">{item._is_misc ? (item.product_name || 'MISC') : item.product_name}</p>
              </div>
              {item._is_misc && (
                <div className="basis-full grid grid-cols-1 md:grid-cols-4 gap-2 p-2 rounded" style={{ background: '#FFF8F2', border: '1px dashed #E89D4F' }} data-testid="edit-misc-fields">
                  <select
                    value={item.sub_brand_name || ''}
                    onChange={(e) => setEditItemSubBrand(idx, e.target.value)}
                    className="px-2 py-1.5 border rounded text-sm bg-white"
                    style={{ borderColor: '#E2DFD8' }}
                    data-testid="edit-misc-subbrand-select"
                  >
                    <option value="">-- Select Sub-Brand --</option>
                    {subBrands.map(sb => (
                      <option key={sb.id} value={sb.name}>{sb.name}</option>
                    ))}
                  </select>
                  <select
                    value={item.parent_product_id || ''}
                    onChange={(e) => setEditItemParent(idx, e.target.value)}
                    className="px-2 py-1.5 border rounded text-sm bg-white"
                    style={{ borderColor: '#E2DFD8' }}
                    data-testid="edit-misc-parent-select"
                  >
                    <option value="">-- Stacks under (Main Product) --</option>
                    {[...products]
                      .filter(p => p.name !== 'MISC' && !p.is_virtual)
                      .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                      .map(p => (
                        <option key={p.id} value={p.id}>{p.name}</option>
                      ))}
                  </select>
                  <div className="flex gap-2 items-center">
                    <select
                      value={[40, 45, 50, 60].includes(Number(item.weight_kg)) && !item._weight_edit ? String(item.weight_kg) : 'edit'}
                      onChange={(e) => {
                        const v = e.target.value;
                        if (v === 'edit') {
                          setEditItemFlag(idx, '_weight_edit', true);
                        } else {
                          setEditItemFlag(idx, '_weight_edit', false);
                          setEditItemWeight(idx, Number(v));
                        }
                      }}
                      className="px-2 py-1.5 border rounded text-sm bg-white"
                      style={{ borderColor: '#E2DFD8' }}
                      data-testid="edit-misc-weight-select"
                    >
                      <option value="40">40 kg</option>
                      <option value="45">45 kg</option>
                      <option value="50">50 kg</option>
                      <option value="60">60 kg</option>
                      <option value="edit">Edit…</option>
                    </select>
                    {(item._weight_edit || ![40, 45, 50, 60].includes(Number(item.weight_kg))) && (
                      <Input
                        type="number"
                        min="1"
                        placeholder="kg"
                        value={item.weight_kg || ''}
                        onChange={(e) => setEditItemWeight(idx, Number(e.target.value) || 0)}
                        className="w-20 text-sm"
                        data-testid="edit-misc-weight-custom-input"
                      />
                    )}
                  </div>
                  <div className="flex gap-2 items-center">
                    <select
                      value={['', 'GUR', 'SPL'].includes(item.variant || '') && !item._variant_edit ? (item.variant || '') : 'edit'}
                      onChange={(e) => {
                        const v = e.target.value;
                        if (v === 'edit') {
                          setEditItemFlag(idx, '_variant_edit', true);
                        } else {
                          setEditItemFlag(idx, '_variant_edit', false);
                          setEditItemVariant(idx, v);
                        }
                      }}
                      className="px-2 py-1.5 border rounded text-sm bg-white"
                      style={{ borderColor: '#E2DFD8', minWidth: '80px' }}
                      data-testid="edit-misc-variant-select"
                      title="Optional variant tag — e.g. GUR / SPL / custom"
                    >
                      <option value="">--</option>
                      <option value="GUR">GUR</option>
                      <option value="SPL">SPL</option>
                      <option value="edit">Edit…</option>
                    </select>
                    {(item._variant_edit || !['', 'GUR', 'SPL'].includes(item.variant || '')) && (
                      <Input
                        type="text"
                        placeholder="Variant"
                        value={item.variant || ''}
                        onChange={(e) => setEditItemVariant(idx, e.target.value.toUpperCase())}
                        className="w-24 text-sm"
                        data-testid="edit-misc-variant-custom-input"
                        autoFocus={item._variant_edit && !item.variant}
                      />
                    )}
                  </div>
                </div>
              )}
              <div className="flex items-center gap-2">
                <HoldRepeatButton size="sm" variant="outline" onFire={() => updateEditItemQty(idx, -1)} data-testid="edit-qty-minus">-</HoldRepeatButton>
                <Input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  enterKeyHint="next"
                  placeholder="1"
                  value={item.quantity === '' || item.quantity == null ? '' : item.quantity}
                  onFocus={(e) => { try { e.target.select(); } catch (_) { /* noop */ } }}
                  onChange={(e) => {
                    const raw = e.target.value.replace(/[^0-9]/g, '');
                    if (raw === '') { setEditItemQty(idx, ''); }
                    else {
                      const n = parseInt(raw, 10);
                      if (!isNaN(n) && n >= 1) setEditItemQty(idx, n);
                    }
                  }}
                  onBlur={(e) => {
                    const n = parseInt(e.target.value, 10);
                    if (isNaN(n) || n < 1) setEditItemQty(idx, 1);
                    else setEditItemQty(idx, n);
                  }}
                  className="w-16 text-center text-sm font-medium px-1"
                  data-testid="edit-qty-input"
                />
                <HoldRepeatButton size="sm" variant="outline" onFire={() => updateEditItemQty(idx, 1)} data-testid="edit-qty-plus">+</HoldRepeatButton>
              </div>
              <div className="flex items-center gap-1">
                <span className="text-xs" style={{ color: '#5C6B61' }}>Disc</span>
                <Input
                  type="text"
                  inputMode="decimal"
                  pattern="[0-9]*[.]?[0-9]*"
                  value={item.discount_percent == null ? '' : item.discount_percent}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v === '' || /^[0-9]*\.?[0-9]*$/.test(v)) setEditItemDiscount(idx, v);
                  }}
                  className="h-8 w-16 text-sm px-2 text-right"
                  data-testid="edit-discount-input"
                  title="Discount % on this line (blank = product default)"
                  placeholder={String(item._default_disc ?? '10')}
                />
                <span className="text-xs" style={{ color: '#5C6B61' }}>%</span>
              </div>
              <div className="flex items-center gap-1 ml-auto">
                <span className="text-xs" style={{ color: '#5C6B61' }}>Rate {'\u20B9'}</span>
                <Input
                  type="text"
                  inputMode="decimal"
                  pattern="[0-9]*[.]?[0-9]*"
                  value={item.price}
                  onFocus={(e) => e.target.select()}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v === '' || /^[0-9]*\.?[0-9]*$/.test(v)) setEditItemPrice(idx, v);
                  }}
                  onBlur={(e) => { const n = parseFloat(e.target.value); if (isNaN(n) || n < 0) setEditItemPrice(idx, 0); }}
                  className="h-8 w-24 text-sm px-2 text-right"
                  data-testid="edit-rate-input"
                  title="Click to edit rate per unit"
                />
              </div>
              <p className="w-24 text-right font-medium text-sm">{'\u20B9'}{Number(item.total || 0).toFixed(2)}</p>
              {editItems.length > 1 && (
                <Button type="button" size="sm" variant="destructive" onClick={() => removeEditItem(idx)} data-testid="edit-remove-item">
                  <Trash2 className="w-3 h-3" />
                </Button>
              )}
            </div>
          ))}
          {/* Add new product */}
          <div className="p-3 rounded border-2 border-dashed" style={{ borderColor: '#E2DFD8' }}>
            <label className="text-xs font-medium mb-1 block" style={{ color: '#5C6B61' }}>Add Product</label>
            <select
              defaultValue=""
              onChange={(e) => {
                if (!e.target.value) return;
                addEditItem(e.target.value);
                e.target.value = '';
                // After React appends the new item (with quantity='') and
                // re-renders, focus + select the LAST qty input. Double-RAF
                // gives React two paint frames to commit the new <input> to
                // the DOM before we look it up.
                requestAnimationFrame(() => requestAnimationFrame(() => {
                  const inputs = document.querySelectorAll('[data-testid="edit-qty-input"]');
                  const last = inputs[inputs.length - 1];
                  if (!last) return;
                  last.focus();
                  try { last.select(); } catch (_) { /* noop */ }
                }));
              }}
              className="w-full px-2 py-1.5 border rounded text-sm"
              style={{ borderColor: '#E2DFD8' }}
              data-testid="edit-add-product-select"
            >
              <option value="">-- Select product to add --</option>
              {[...products]
                .filter(p => !p.is_virtual)
                .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                .map(p => (
                  <option key={p.id} value={p.id}>{p.name}{editItems.some(ei => ei.product_id === p.id) ? ' (already in order)' : ''}</option>
                ))}
            </select>
          </div>
          {/* Delivery Schedule (Reschedule inside Edit) */}
          <div className="p-3 rounded" style={{ background: '#FBF9F6' }}>
            <p className="text-xs font-bold mb-2 uppercase tracking-wider" style={{ color: '#2B5339' }}>Delivery Schedule</p>
            {(selectedOrder?.status === 'dispatched' || selectedOrder?.status === 'delivered' || selectedOrder?.status === 'partial_dispatched') && !isStrictAdmin ? (
              <div className="text-xs p-2 rounded" style={{ background: '#FFF5F0', color: '#C05C35', border: '1px solid #E89D4F' }} data-testid="reschedule-locked-note">
                <span className="font-semibold">Locked:</span> Dispatched orders cannot be rescheduled. Current: {fmtDate(selectedOrder.delivery_date) || '-'} at {selectedOrder?.delivery_time || '-'}.
              </div>
            ) : (
              <>
                {(selectedOrder?.status === 'dispatched' || selectedOrder?.status === 'delivered' || selectedOrder?.status === 'partial_dispatched') && (
                  <div className="text-xs p-2 mb-2 rounded" style={{ background: '#FFF8E7', color: '#8A6D00', border: '1px solid #E0C36C' }} data-testid="admin-override-note">
                    <span className="font-semibold">Admin override:</span> You are editing a {selectedOrder.status} order. Stock / dispatch records are NOT auto-adjusted — use with caution.
                  </div>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-medium block mb-1" style={{ color: '#5C6B61' }}>Delivery Date <span style={{ color: '#C05C35' }}>*</span></label>
                    <Input
                      type="date"
                      required
                      value={editDeliveryDate}
                      onChange={(e) => setEditDeliveryDate(e.target.value)}
                      className="w-full"
                      data-testid="edit-delivery-date-input"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium block mb-1" style={{ color: '#5C6B61' }}>Delivery Time</label>
                    <Input
                      type="time"
                      value={editDeliveryTime}
                      onChange={(e) => setEditDeliveryTime(e.target.value)}
                      className="w-full"
                      data-testid="edit-delivery-time-input"
                    />
                  </div>
                </div>
                {selectedOrder?.delivery_date && (
                  <p className="text-xs mt-2" style={{ color: '#5C6B61' }}>
                    Current: {fmtDate(selectedOrder.delivery_date)} at {selectedOrder.delivery_time || '-'}
                  </p>
                )}
              </>
            )}
          </div>
          {/* Totals */}
          <div className="pt-3" style={{ borderTop: '2px solid #E2DFD8' }}>
            <div className="flex justify-between text-sm mb-1">
              <span style={{ color: '#5C6B61' }}>Gross Total</span>
              <span>{'\u20B9'}{editItems.reduce((s, i) => s + i.total, 0).toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-sm mb-1" style={{ color: '#C05C35' }}>
              <span>Product-wise Discount</span>
              <span>- {'\u20B9'}{editItems.reduce((s, i) => {
                const pct = i.discount_percent === '' || i.discount_percent == null ? (productDiscountByPid[i.product_id] ?? 10) : Number(i.discount_percent);
                return s + (i.total * pct / 100);
              }, 0).toFixed(2)}</span>
            </div>
            <div className="flex justify-between font-bold text-lg">
              <span>Net Total</span>
              <span style={{ color: '#2B5339' }} data-testid="edit-new-total">{'\u20B9'}{editItems.reduce((s, i) => {
                const pct = i.discount_percent === '' || i.discount_percent == null ? (productDiscountByPid[i.product_id] ?? 10) : Number(i.discount_percent);
                return s + (i.total * (1 - pct / 100));
              }, 0).toFixed(2)}</span>
            </div>
          </div>
        </div>
      </div>
      <DialogFooter className="px-6 py-4 border-t flex-shrink-0" style={{ borderColor: '#E2DFD8' }}>
        <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
        <Button type="submit" style={{ background: '#2B5339' }} data-testid="save-edit-btn">Save Changes</Button>
      </DialogFooter>
      </form>
    </DialogContent>
  </Dialog>
);
